import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { vdjColor, vdjBeatChroma, vdjBarChroma, vdjBeatBreath, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les faisceaux sont calculés par pixel.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'haze',
  family: 'shader',
  danceability: 0.60,
  requiresTier: 0,
  energyLevel: 0.45,
  bpmMin: 90,
  bpmMax: 128,
  movement: 0.45,
  tonality: 0.70,
  enabled: true
};

// HAZE : une salle remplie de fumée qui tourbillonne lentement, traversée par
// des faisceaux de lumière qui la SCULPTENT : la lumière n'existe que là où la
// fumée est dense, et la musique pousse la fumée dans la lumière. Le moment
// juste avant que la fête démarre. Deep house, techno mélodique, ambient,
// downtempo : preset lounge de vdjLook (souffle sur le temps, séparation RGB
// sur la mesure, aucun flash) ; au-dessus de 0,7 d'intensité, le preset party.
//
// LA FUMÉE : le fluide stable d'Oilshow (vitesse en texels/s sur 192 x 108,
// demi-flottants lus en bilinéaire manuel, pression par Jacobi), une densité
// de fumée RGBA8 en 384 x 216 advectée avec une légère dissipation, une
// FLOTTABILITÉ (la fumée monte), un tourbillon lent par bruit de courbure.
// Deux machines au sol, à gauche et à droite, soufflent par bouffées sur le
// kick (densité + impulsion vers le haut et le centre). À l'affichage, un fbm
// à trois octaves, dérivant avec le temps, module la densité : la fumée a des
// volutes nettes, pas un flou. Simulation préchauffée (90 pas) : la salle est
// enfumée dès la première image.
//
// LES FAISCEAUX, par pixel : 3 à 6 projecteurs (au plafond en éventail, ou
// derrière, d'un point de fuite), chacun un cône 2D (profil gaussien en
// travers, atténuation exponentielle le long) ; l'intensité du pixel est la
// somme des faisceaux qui le traversent, MULTIPLIÉE par la densité locale :
// sans fumée, rien. Une couleur de palette par faisceau. Une NAPPE laser
// horizontale coupe la fumée en tranche (tiers 1 et 2). Une fois par scène,
// vers 10 s, un faisceau central large projette la pochette dans la fumée
// (gobo), au centre de composition (R19), en fondu sur 6 s.
//
// LA MUSIQUE : temps → bouffée sur le kick (machines en alternance) et souffle
// des faisceaux ; mesure → balayage vers une nouvelle position (vitesse
// angulaire bornée) ; phrase → palette et disposition ; drop (intensité > 0,7)
// → les faisceaux convergent au centre, nappe, bouffée forte.

const TIERS = {
  0: { w: 128, h: 72, dye: 2, iter: 12, beams: 3, sheet: false },
  1: { w: 160, h: 90, dye: 2, iter: 16, beams: 4, sheet: true },
  2: { w: 192, h: 108, dye: 2, iter: 20, beams: 5, sheet: true }
};
const MAX_BEAMS = 6;
const VMAX = 400;
const SCENE_S = 40;
const GOBO_AT_S = 10, GOBO_S = 6;
const NO_BPM = 110;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }
const smooth01 = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };

// --- GLSL ------------------------------------------------------------------------------
const quadVS = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const VEL_CHUNK = `
  uniform float uEnc;
  vec2 decV(vec4 t) { return uEnc > 0.5 ? (t.xy - 0.5) * ${(2 * VMAX).toFixed(1)} : t.xy; }
  vec4 encV(vec2 v) { return uEnc > 0.5 ? vec4(clamp(v / ${(2 * VMAX).toFixed(1)} + 0.5, 0.0, 1.0), 0.0, 1.0) : vec4(v, 0.0, 1.0); }
  float decS(vec4 t) { return uEnc > 0.5 ? (t.x - 0.5) * ${(2 * VMAX).toFixed(1)} : t.x; }
  vec4 encS(float s) { return uEnc > 0.5 ? vec4(clamp(s / ${(2 * VMAX).toFixed(1)} + 0.5, 0.0, 1.0), 0.0, 0.0, 1.0) : vec4(s, 0.0, 0.0, 1.0); }
  vec2 velAt(sampler2D v, vec2 uv, vec2 texel) {
    vec2 p = uv / texel - 0.5;
    vec2 i = floor(p), f = p - i;
    vec2 a = decV(texture2D(v, (i + 0.5) * texel)), b = decV(texture2D(v, (i + vec2(1.5, 0.5)) * texel));
    vec2 c = decV(texture2D(v, (i + vec2(0.5, 1.5)) * texel)), d = decV(texture2D(v, (i + 1.5) * texel));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
`;
const advectVelFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel;
  uniform vec2 uTexel;
  uniform float uDt, uDecay;
  varying vec2 vUv;
  void main() {
    vec2 v = velAt(uVel, vUv, uTexel);
    vec2 pos = clamp(vUv - v * uDt * uTexel, uTexel * 0.5, 1.0 - uTexel * 0.5);
    vec2 nv = velAt(uVel, pos, uTexel) * uDecay;
    vec2 b = step(uTexel * 1.5, vUv) * step(vUv, 1.0 - uTexel * 1.5);
    gl_FragColor = encV(nv * b.x * b.y);
  }
`;
// forces: the machines' puffs, buoyancy from the smoke, a slow curl-noise swirl
const forcesFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel, uDye, uNoise;
  uniform vec2 uTexel;
  uniform float uAspect, uDt, uH, uT, uSwirl, uBuoy;
  uniform vec4 uF[4];          // x, y (square space), strength (texels/s per 0.1 s), radius
  uniform vec4 uFt[4];         // dir.x, dir.y, unused, unused
  varying vec2 vUv;
  float vn(vec2 p) { return texture2D(uNoise, p).r; }
  void main() {
    vec2 v = decV(texture2D(uVel, vUv));
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    for (int i = 0; i < 4; i++) {
      vec4 f = uF[i];
      if (f.z == 0.0) continue;
      vec2 d = p - f.xy;
      float g = exp(-dot(d, d) / (f.w * f.w));
      v += uFt[i].xy * f.z * g * uDt * 10.0;
    }
    // buoyancy: the smoke rises, more where it is dense
    float dens = texture2D(uDye, vUv).r;
    v.y += uBuoy * dens * uDt * uH;
    // a slow swirl: the curl of a drifting noise
    vec2 q = p * 0.45 + vec2(uT * 0.012, -uT * 0.009);
    float e = 0.01;
    float nx = vn(q + vec2(e, 0.0)) - vn(q - vec2(e, 0.0)), ny = vn(q + vec2(0.0, e)) - vn(q - vec2(0.0, e));
    v += vec2(ny, -nx) / (2.0 * e) * uSwirl * uDt * uH * 0.02;
    gl_FragColor = encV(v);
  }
`;
const divergenceFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float l = decV(texture2D(uVel, vUv - vec2(uTexel.x, 0.0))).x, r = decV(texture2D(uVel, vUv + vec2(uTexel.x, 0.0))).x;
    float b = decV(texture2D(uVel, vUv - vec2(0.0, uTexel.y))).y, t = decV(texture2D(uVel, vUv + vec2(0.0, uTexel.y))).y;
    gl_FragColor = encS(0.5 * (r - l + t - b));
  }
`;
const jacobiFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uP, uDiv;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float l = decS(texture2D(uP, vUv - vec2(uTexel.x, 0.0))), r = decS(texture2D(uP, vUv + vec2(uTexel.x, 0.0)));
    float b = decS(texture2D(uP, vUv - vec2(0.0, uTexel.y))), t = decS(texture2D(uP, vUv + vec2(0.0, uTexel.y)));
    gl_FragColor = encS((l + r + b + t - decS(texture2D(uDiv, vUv))) * 0.25);
  }
`;
const gradientFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel, uP;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float l = decS(texture2D(uP, vUv - vec2(uTexel.x, 0.0))), r = decS(texture2D(uP, vUv + vec2(uTexel.x, 0.0)));
    float b = decS(texture2D(uP, vUv - vec2(0.0, uTexel.y))), t = decS(texture2D(uP, vUv + vec2(0.0, uTexel.y)));
    gl_FragColor = encV(decV(texture2D(uVel, vUv)) - 0.5 * vec2(r - l, t - b));
  }
`;
// the smoke density: advected, dissipating, fed by the machines
const dyeFS = `
  precision highp float;
  ${VEL_CHUNK}
  uniform sampler2D uVel, uDye;
  uniform vec2 uTexel, uDyeTexel;
  uniform float uAspect, uDt, uDecay;
  uniform vec4 uSplat[2];      // x, y, radius, amount
  varying vec2 vUv;
  void main() {
    vec2 v = velAt(uVel, vUv, uTexel);
    vec2 pos = clamp(vUv - v * uDt * uTexel, uDyeTexel * 0.5, 1.0 - uDyeTexel * 0.5);
    float d = texture2D(uDye, pos).r * uDecay;
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    for (int i = 0; i < 2; i++) {
      vec4 sp = uSplat[i];
      if (sp.w == 0.0) continue;
      vec2 q = p - sp.xy;
      d += sp.w * exp(-dot(q, q) / (sp.z * sp.z));
    }
    gl_FragColor = vec4(clamp(d, 0.0, 1.0), 0.0, 0.0, 1.0);
  }
`;
// the room: beams through the smoke, a sheet, the gobo
const displayFS = `
  precision highp float;
  uniform sampler2D uDye, uNoise, uArt;
  uniform float uAspect, uT, uGain, uAmbient, uBeamN, uSheet, uSheetY, uGobo, uGoboSize, uHasArt, uDetail;
  uniform vec4 uBeam[${MAX_BEAMS}];       // origin x, y (square space), angle (rad), half angle (rad)
  uniform vec4 uBeamC[${MAX_BEAMS}];      // colour rgb, intensity
  uniform vec3 uSheetC;
  uniform vec2 uStage, uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float vn(vec2 p) { return texture2D(uNoise, p).r; }
  float fbm(vec2 p) { float s = 0.0, a = 0.5; for (int i = 0; i < 3; i++) { s += a * vn(p); p = p * 2.1 + 0.37; a *= 0.5; } return s / 0.875; }
  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    // the smoke: the simulated density, carved by a drifting fbm (volutes, not a blur)
    float base = texture2D(uDye, vUv).r;
    float n1 = fbm(p * 1.6 + vec2(uT * 0.02, uT * 0.035));
    float n2 = fbm(p * 4.5 - vec2(uT * 0.05, -uT * 0.02));
    float dens = base * clamp(0.35 + 0.75 * n1 + 0.5 * (n2 - 0.5) * uDetail, 0.0, 1.6);
    dens = pow(dens, 1.15);
    vec3 col = vec3(0.55, 0.62, 0.72) * uAmbient * dens;
    // the beams: a cone each, visible only where there is smoke
    for (int i = 0; i < ${MAX_BEAMS}; i++) {
      if (float(i) >= uBeamN) break;
      vec4 b = uBeam[i];
      vec2 d = p - b.xy;
      vec2 dir = vec2(cos(b.z), sin(b.z));
      float along = dot(d, dir);
      if (along <= 0.0) continue;
      float perp = d.x * dir.y - d.y * dir.x;
      float w = along * tan(b.w) + 0.012;
      float prof = exp(-perp * perp / (w * w) * 1.8);
      float att = exp(-along * 0.55) * smoothstep(0.0, 0.06, along);
      col += uBeamC[i].rgb * uBeamC[i].a * prof * att * dens * 1.6;
    }
    // the laser sheet: a horizontal slice through the smoke
    if (uSheet > 0.0) {
      float s = exp(-pow((p.y - uSheetY) / 0.010, 2.0));
      col += uSheetC * s * dens * 2.2 * uSheet;
    }
    // the gobo: the cover projected into the smoke by a wide central beam (stage centre, R19)
    if (uGobo > 0.0 && uHasArt > 0.5) {
      vec2 g = (p - uStage) / uGoboSize + 0.5;
      if (g.x > 0.0 && g.x < 1.0 && g.y > 0.0 && g.y < 1.0) {
        vec3 art = texture2D(uArt, vec2(g.x, g.y)).rgb;
        float edge = smoothstep(0.0, 0.08, g.x) * smoothstep(1.0, 0.92, g.x) * smoothstep(0.0, 0.08, g.y) * smoothstep(1.0, 0.92, g.y);
        col += art * edge * dens * uGobo * 1.1;
      }
    }
    // the cover's mask follows the cover (R19); the safe-zone uniforms are in half-height units
    // (y from -1 to 1), p spans -0.5..0.5: twice p, or the mask is twice too big and misplaced
    vec2 qq = abs(p * 2.0 - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;

export class HazeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    const nb = parseInt(urlParam('hazebeams'), 10);
    this._nBeams = Number.isFinite(nb) ? Math.max(1, Math.min(MAX_BEAMS, nb)) : this._T.beams;
    this._dbgSheet = urlParam('hazesheet') === '1';
    this._dbgGobo = urlParam('hazegobo') === '1';
    this._dbgPuff = urlParam('hazepuff') === '1';
    this._adapt = new Adapt();
    this._post = new VdjPost({ bloomStrength: 0.3, bloomThreshold: 0.8, grain: 0.02, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.lounge.comfort);
    if (urlParam('hazeprobe') === '1' && typeof window !== 'undefined') window.__haze = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const T = this._T;
    this._forces = []; this._splats = [];
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._simScene = new THREE.Scene();
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), null);
    this._quad.frustumCulled = false;
    this._simScene.add(this._quad);
    const mk = (fs, uniforms) => new THREE.ShaderMaterial({ uniforms, vertexShader: quadVS, fragmentShader: fs, depthTest: false, depthWrite: false });
    const enc = { value: 0 }, texel = { value: new THREE.Vector2(1 / T.w, 1 / T.h) }, dyeTexel = { value: new THREE.Vector2(1 / (T.w * T.dye), 1 / (T.h * T.dye)) };
    this._enc = enc; this._texel = texel; this._dyeTexel = dyeTexel;
    this._aspect = { value: 1.78 };
    // a tileable value noise
    const nz = new Uint8Array(256 * 256 * 4); let sd = 31; const rnd = () => { sd = (Math.imul(sd, 1664525) + 1013904223) >>> 0; return sd / 4294967296; };
    for (let i = 0; i < nz.length; i++) nz[i] = Math.floor(rnd() * 256);
    this._noise = new THREE.DataTexture(nz, 256, 256, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._noise.minFilter = this._noise.magFilter = THREE.LinearFilter; this._noise.wrapS = this._noise.wrapT = THREE.RepeatWrapping; this._noise.needsUpdate = true;
    this._mats = {
      advect: mk(advectVelFS, { uEnc: enc, uVel: { value: null }, uTexel: texel, uDt: { value: 0 }, uDecay: { value: 1 } }),
      forces: mk(forcesFS, { uEnc: enc, uVel: { value: null }, uDye: { value: null }, uNoise: { value: this._noise }, uTexel: texel, uAspect: this._aspect, uDt: { value: 0 }, uH: { value: T.h }, uT: { value: 0 }, uSwirl: { value: 1 }, uBuoy: { value: 0.9 },
        uF: { value: Array.from({ length: 4 }, () => new THREE.Vector4()) }, uFt: { value: Array.from({ length: 4 }, () => new THREE.Vector4()) } }),
      div: mk(divergenceFS, { uEnc: enc, uVel: { value: null }, uTexel: texel }),
      jacobi: mk(jacobiFS, { uEnc: enc, uP: { value: null }, uDiv: { value: null }, uTexel: texel }),
      grad: mk(gradientFS, { uEnc: enc, uVel: { value: null }, uP: { value: null }, uTexel: texel }),
      dye: mk(dyeFS, { uEnc: enc, uVel: { value: null }, uDye: { value: null }, uTexel: texel, uDyeTexel: dyeTexel, uAspect: this._aspect, uDt: { value: 0 }, uDecay: { value: 1 }, uSplat: { value: [new THREE.Vector4(), new THREE.Vector4()] } })
    };
    this._dispU = {
      uDye: { value: null }, uNoise: { value: this._noise }, uArt: { value: null }, uHasArt: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uT: { value: 0 }, uGain: { value: 1 }, uAmbient: { value: 0.025 }, uBeamN: { value: this._nBeams }, uDetail: { value: 1 },
      uSheet: { value: 0 }, uSheetY: { value: 0.1 }, uSheetC: { value: new THREE.Color(0.2, 1, 0.4) }, uGobo: { value: 0 }, uGoboSize: { value: 0.9 },
      uBeam: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector4(0, 1.2, -Math.PI / 2, 0.08)) }, uBeamC: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector4(1, 1, 1, 0)) },
      uStage: safeZoneUniforms.uStageCenter, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mk(displayFS, this._dispU));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._seed = 3; this._trackSeen = undefined; this._artUrl = null;
    this._time = 0; this._pending = 0; this._freePhase = 0; this._beats = 0; this._machine = 0;
    this._intensity = 0.3; this._kickT = -9; this._dropUntil = -1; this._sheet = 0; this._gobo = 0; this._goboDone = false;
    this._layout = 0; this._palShift = 0;
    this._beams = Array.from({ length: MAX_BEAMS }, (_, i) => ({ ox: 0, oy: 1.2, ang: -Math.PI / 2, target: -Math.PI / 2, half: 0.07, pulse: 1 }));
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._warm = false; this._targets = null;
    this._log = [];
    this._placeBeams(true);
  }

  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(e) { e.t = +this._time.toFixed(2); this._log.push(e); if (this._log.length > 30) this._log.shift(); }

  // a layout: on the ceiling in a fan, or behind, from a vanishing point
  _placeBeams(snap) {
    const a = this._aspect.value, n = this._nBeams;
    for (let i = 0; i < n; i++) {
      const b = this._beams[i], u = n > 1 ? i / (n - 1) : 0.5;
      if (this._layout === 0) { b.ox = (u - 0.5) * a * 0.85; b.oy = 0.62; b.target = -Math.PI / 2 + (0.5 - u) * 0.9 + (this._rnd() - 0.5) * 0.3; b.half = 0.055 + 0.03 * this._rnd(); }
      else { b.ox = (this._rnd() - 0.5) * 0.3; b.oy = 0.35; b.target = -Math.PI / 2 + (u - 0.5) * 1.9 + (this._rnd() - 0.5) * 0.2; b.half = 0.05 + 0.025 * this._rnd(); }
      if (snap) b.ang = b.target;
    }
  }
  _sweep() { const n = this._nBeams; for (let i = 0; i < n; i++) { const b = this._beams[i]; b.target += (this._rnd() - 0.5) * 0.5; const lim = this._layout === 0 ? 0.75 : 1.2; const c = -Math.PI / 2 + (this._layout === 0 ? (0.5 - (n > 1 ? i / (n - 1) : 0.5)) * 0.9 : ((n > 1 ? i / (n - 1) : 0.5) - 0.5) * 1.9); b.target = Math.max(c - lim, Math.min(c + lim, b.target)); } }
  _puff(side, strength) {
    const a = this._aspect.value;
    const x = (side < 0 ? -1 : 1) * Math.min(0.4 * a, 0.6), y = -0.52;        // never farther than 0.6 from the centre (wide strips)
    const narrow = Math.min(1, a / 1.5);                                   // a narrow frame fills up faster
    this._splats.push({ x, y, r: 0.07 + 0.03 * strength, amount: (0.55 + 0.35 * strength) * narrow, frames: 5 });
    this._forces.push({ x, y, strength: 140 + 160 * strength, radius: 0.12, dir: [side < 0 ? 0.35 : -0.35, 1.0], until: this._time + 0.16 });
  }

  _colours(profile, a) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const hsl = [];
    const sat = this._intensity > 0.7 ? 0.9 : 0.85;
    for (let k = 0; k < 3; k++) {
      const c = _tone[k];
      if (src.length) vdjColor(src[(k + this._palShift) % src.length], c, sat); else c.setHSL((0.6 + k * 0.33 + this._palShift * 0.1) % 1, 0.85, 0.55);
      const o = { h: 0, s: 0, l: 0 }; c.getHSL(o); hsl.push(o);
    }
    for (let k = 1; k < 3; k++) for (let j = 0; j < k; j++) { const d = Math.abs(hsl[k].h - hsl[j].h); if (Math.min(d, 1 - d) < 0.25) { hsl[k].h = (hsl[0].h + k / 3) % 1; _tone[k].setHSL(hsl[k].h, sat, 0.55); } }
    for (let i = 0; i < MAX_BEAMS; i++) { const c = _tone[i % 3]; this._dispU.uBeamC.value[i].x = c.r; this._dispU.uBeamC.value[i].y = c.g; this._dispU.uBeamC.value[i].z = c.b; }
    this._dispU.uSheetC.value.copy(_tone[1]);
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 3;
    }
    if (profile.artworkUrl && profile.artworkUrl !== this._artUrl) {
      const url = this._artUrl = profile.artworkUrl;
      loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
        if (this._artUrl !== url || !this._scene) { tex.dispose(); return; }
        if (this._dispU.uArt.value) this._dispU.uArt.value.dispose();
        this._dispU.uArt.value = tex; this._dispU.uHasArt.value = 1;
      });
    }
    this._colours(profile, a);
    if (!playing) { this._pending = 0; return; }           // frozen, never black
    this._time += dt; this._pending += dt;
    this._clock.update(dt);
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 2);
    const I = this._intensity, t = this._time, party = I > 0.7;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    const pulsed = a.pulse > 0.25;
    let beat = a.beat, phase;
    if (pulsed) phase = a.beatPhase;
    else { this._freePhase += dt / beatS; if (this._freePhase >= 1) { this._freePhase -= 1; beat = true; } phase = this._freePhase; }
    if (beat) { this._beats++; this._lastBeatT = t; }
    const bar = a.bar || (!pulsed && beat && this._beats % 4 === 0);
    const phrase = a.phrase || (!pulsed && beat && this._beats % 16 === 0);
    const b = a.bands || { low: 0.3 + 0.3 * I, mid: 0.3, high: 0.2 };
    if (bar) this._lastBarT = t;

    // the scene: a fresh room, the gobo once around 10 s
    if (this._clock.wrapped) { this._goboDone = false; this._layout = this._rnd() < 0.5 ? 0 : 1; this._placeBeams(false); }
    const ct = this._clock.t;
    const goboOn = this._dbgGobo || (ct > GOBO_AT_S && ct < GOBO_AT_S + GOBO_S);
    this._gobo += ((goboOn ? 1 : 0) - this._gobo) * Math.min(1, dt / 1.5);
    // the kick: a puff from one machine, alternating; the beams breathe
    const kick = (beat && (party || b.low > 0.35)) || (a.onset > 0.7 && b.low > 0.45 && t - this._kickT > 0.3);
    if ((kick && t - this._kickT > 0.25) || (this._dbgPuff && Math.floor(t / 2) !== Math.floor((t - dt) / 2))) {
      this._kickT = t; this._machine = 1 - this._machine;
      this._puff(this._machine ? 1 : -1, Math.min(1, b.low + 0.3 * I));
    }
    // the bar: a sweep; the phrase: palette and layout
    if (bar) this._sweep();
    if (phrase) { this._palShift = (this._palShift + 1) % 3; this._layout = this._rnd() < 0.5 ? 0 : 1; this._placeBeams(false); this._note({ what: 'phrase', layout: this._layout }); }
    // the drop (party only): all beams converge, the sheet, a strong puff from both machines
    if (a.drop && party) { this._dropUntil = t + 8 * beatS; this._puff(-1, 1); this._puff(1, 1); this._note({ what: 'drop' }); }
    const inDrop = t < this._dropUntil;
    const wantSheet = (this._T.sheet && inDrop) || this._dbgSheet ? 1 : 0;
    this._sheet += (wantSheet - this._sheet) * Math.min(1, dt / 0.6);
    // the beams: angles chase their targets at a bounded speed (a slow sweep); in the drop they aim at the centre
    const vmax = (inDrop ? 1.4 : 0.35) * dt;
    const U = this._dispU;
    const breath = vdjBeatBreath(t - (this._lastBeatT || -9), VDJ_PRESETS.lounge);
    for (let i = 0; i < this._nBeams; i++) {
      const be = this._beams[i];
      let tg = be.target;
      if (inDrop) { const sx = safeZoneUniforms.uStageCenter.value.x, sy = safeZoneUniforms.uStageCenter.value.y; tg = Math.atan2(sy - be.oy, sx - be.ox); }
      let d = tg - be.ang; d = Math.atan2(Math.sin(d), Math.cos(d));
      be.ang += Math.max(-vmax, Math.min(vmax, d));
      U.uBeam.value[i].set(be.ox, be.oy, be.ang, be.half * Math.pow(Math.min(1, this._aspect.value / 1.78), 0.7));   // narrower cones in narrow frames
      const pulse = party ? 1 + 0.35 * Math.exp(-phase * 5) : breath;
      U.uBeamC.value[i].w = (0.55 + 0.45 * I) * pulse * (0.85 + 0.15 * Math.sin(t * 0.7 + i * 1.9));
    }
    U.uSheet.value = this._sheet; U.uSheetY.value = 0.05 + 0.12 * Math.sin(t * 0.4);
    U.uGobo.value = this._gobo * 0.28; U.uGoboSize.value = Math.min(0.9, this._aspect.value * 0.8); U.uT.value = t;
    U.uGain.value = Math.min(1, a.lumaCap / 0.33);
    this._forces = this._forces.filter((f) => f.until > t);
    this._swirl = 0.6 + 0.8 * I; this._buoy = 0.7 + 0.5 * I;
    this._post.update(party ? vdjBeatChroma(phase, beatS) : vdjBarChroma(t - (this._lastBarT || -9)));
  }

  _ensureTargets(renderer, aspect) {
    const T = this._T;
    const portrait = aspect < 1;
    const w = portrait ? T.h : T.w, h = portrait ? T.w : T.h;
    if (this._targets && this._targets.w === w) return;
    if (this._targets) for (const k of ['v0', 'v1', 'p0', 'p1', 'div', 'd0', 'd1']) this._targets[k].dispose();
    const ext = renderer.extensions;
    const floatOk = renderer.capabilities.isWebGL2 && (ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float'));
    this._enc.value = floatOk ? 0 : 1;
    const simOpts = { type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping };
    const dyeOpts = { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping };
    const mkS = () => new THREE.WebGLRenderTarget(w, h, simOpts), mkD = () => new THREE.WebGLRenderTarget(w * T.dye, h * T.dye, dyeOpts);
    this._targets = { w, h, v0: mkS(), v1: mkS(), p0: mkS(), p1: mkS(), div: mkS(), d0: mkD(), d1: mkD() };
    this._texel.value.set(1 / w, 1 / h); this._dyeTexel.value.set(1 / (w * T.dye), 1 / (h * T.dye));
    this._mats.forces.uniforms.uH.value = h;
    this._warm = false;
  }

  _pass(renderer, mat, target) {
    this._quad.material = mat;
    renderer.setRenderTarget(target);
    renderer.render(this._simScene, this._ortho);
  }

  _step(renderer, dt) {
    const G = this._targets, M = this._mats, T = this._T;
    const decay = Math.pow(0.5, dt / 3.0), dyeDecay = Math.pow(0.5, dt / (14 * Math.min(1, this._aspect.value / 1.5)));   // a narrow frame clears faster
    M.advect.uniforms.uVel.value = G.v0.texture; M.advect.uniforms.uDt.value = dt; M.advect.uniforms.uDecay.value = decay;
    this._pass(renderer, M.advect, G.v1);
    const F = M.forces.uniforms;
    for (let i = 0; i < 4; i++) {
      const f = this._forces[i];
      if (f) { F.uF.value[i].set(f.x, f.y, f.strength, f.radius); F.uFt.value[i].set(f.dir[0], f.dir[1], 0, 0); }
      else F.uF.value[i].set(0, 0, 0, 1);
    }
    F.uVel.value = G.v1.texture; F.uDye.value = G.d0.texture; F.uDt.value = dt; F.uT.value = this._time;
    F.uSwirl.value = this._swirl || 0.8; F.uBuoy.value = this._buoy || 0.8;
    this._pass(renderer, M.forces, G.v0);
    M.div.uniforms.uVel.value = G.v0.texture;
    this._pass(renderer, M.div, G.div);
    let pr = G.p0, pw = G.p1;
    M.jacobi.uniforms.uDiv.value = G.div.texture;
    for (let i = 0; i < T.iter; i++) {
      M.jacobi.uniforms.uP.value = pr.texture;
      this._pass(renderer, M.jacobi, pw);
      const tmp = pr; pr = pw; pw = tmp;
    }
    M.grad.uniforms.uVel.value = G.v0.texture; M.grad.uniforms.uP.value = pr.texture;
    this._pass(renderer, M.grad, G.v1);
    const tv = G.v0; G.v0 = G.v1; G.v1 = tv;
    const D = M.dye.uniforms;
    D.uVel.value = G.v0.texture; D.uDt.value = dt; D.uDecay.value = dyeDecay;
    for (let i = 0; i < 2; i++) {
      const sp = this._splats[i];
      if (sp) D.uSplat.value[i].set(sp.x, sp.y, sp.r, sp.amount * dt * 10); else D.uSplat.value[i].set(0, 0, 1, 0);
    }
    D.uDye.value = G.d0.texture;
    this._pass(renderer, M.dye, G.d1);
    for (const sp of this._splats) sp.frames--;
    this._splats = this._splats.filter((sp) => sp.frames > 0);
    const td = G.d0; G.d0 = G.d1; G.d1 = td;
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const bsz = renderer.getDrawingBufferSize(_size); w = bsz.x; h = bsz.y; }
    this._aspect.value = w / h;
    this._ensureTargets(renderer, w / h);
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    if (!this._warm) {
      // the room is already smoky at the first frame: puffs from both machines, 90 steps
      this._warm = true;
      renderer.setClearColor(0x000000, 0);
      for (const k of ['v0', 'v1', 'p0', 'p1', 'div', 'd0', 'd1']) { renderer.setRenderTarget(this._targets[k]); renderer.clear(true, false, false); }
      this._placeBeams(true);
      for (let i = 0; i < 90; i++) {
        if (i % 18 === 0) this._puff(i % 36 === 0 ? -1 : 1, 0.9);
        this._step(renderer, 1 / 60);
        this._forces = this._forces.filter((f) => f.until > this._time + (i - 90) / 60);
      }
      this._pending = 0;
    }
    let n = 0;
    while (this._pending > 1e-4 && n < 3) { const dt = Math.min(this._pending, 1 / 30); this._step(renderer, dt); this._pending -= dt; n++; }
    this._pending = 0;
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uDye.value = this._targets.d0.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    if (this._display) { this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._quad) this._quad.geometry.dispose();
    if (this._mats) for (const k in this._mats) this._mats[k].dispose();
    if (this._targets) for (const k of ['v0', 'v1', 'p0', 'p1', 'div', 'd0', 'd1']) this._targets[k].dispose();
    if (this._noise) this._noise.dispose();
    if (this._dispU && this._dispU.uArt.value) this._dispU.uArt.value.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__haze === this) window.__haze = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _tone = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
